/**
 * „Aus dem Angebot" — zuerst aufteilen, dann Tage planen (Benjamin, 02.10.2026).
 *
 * Je Packzettel eine Zeile: wer führt aus (Helfer), gegen welche Pauschale, wie
 * viele Leistungen und wie viele davon schon an einem Tag stehen. Darunter das
 * eigene Team (alles, was in keinem Packzettel steht) mit „Packzettel anlegen",
 * und das Material, das nicht verteilt wird, sondern als Lieferung an einen Tag kommt.
 */
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { fehlerText } from "@vimaana/ui";

import PackzettelDialog from "../invoices/PackzettelDialog";
import { usePackzettelAusfuehrung } from "../../hooks/useArbeitsplan";
import { useAddPauschale, useProjektPauschalen, useTeamMembers } from "../../hooks/usePlanung";
import { formatEur } from "../../lib/planer";
import MaterialBlock from "./MaterialBlock";
import { positionsNummer, type Arbeitsplan, type PlanPackzettel } from "../../lib/arbeitsplan";
import type { PackzettelPosition } from "../../lib/packzettel";

export default function AufteilungBlock({ plan }: { plan: Arbeitsplan }) {
  const qc = useQueryClient();
  const { data: members = [] } = useTeamMembers();
  const { data: pauschalen = [] } = useProjektPauschalen(plan.projekt_id);
  const [auswahl, setAuswahl] = useState<string[] | null>(null);
  const [dialog, setDialog] = useState<PackzettelPosition[] | null>(null);
  const positionen = plan.angebot?.positionen || [];
  const helfer = useMemo(
    () => members.filter((m) => m.active !== false && m.role !== "inhaber").map((m) => ({ id: m.id, name: m.name }))
      .sort((a, b) => a.name.localeCompare(b.name, "de")),
    [members],
  );

  if (!plan.angebot) {
    return <p className="apl-leise">Ohne Angebot gibt es nichts aufzuteilen — Tage lassen sich trotzdem planen.</p>;
  }
  const eigen = positionen.filter((p) => !p.packzettel);

  const weiter = () => {
    const gewaehlt = positionen.filter((p) => auswahl?.includes(p.id));
    setDialog(gewaehlt.map((p) => ({
      titel: p.titel || "Leistung", beschreibung: "", menge: p.menge, einheit: p.einheit || "Stück",
      angebot_netto: p.gesamt_netto, budget_netto: 0, position_anker: p.id,
    })));
    setAuswahl(null);
  };

  return (
    <div className="apl-aufteilung">
      <div className="apl-cap">Aus dem Angebot {plan.angebot.nummer}</div>
      <ul className="apl-zeilen">
        {plan.packzettel.map((z) => (
          <ZettelZeile key={z.id} plan={plan} z={z} helfer={helfer} pauschalen={pauschalen} />
        ))}
        <li className="apl-zeile">
          <div className="apl-zeile-text">
            <strong>Eigenes Team</strong>
            <small>
              {eigen.length === 1 ? "1 Leistung" : `${eigen.length} Leistungen`} nach Stunden
              {eigen.length > 0 && ` · ${eigen.filter((p) => p.tage.length).length} an einem Tag`}
            </small>
          </div>
          {eigen.length > 0 && (
            <button type="button" className="vg-btn" onClick={() => setAuswahl([])}>Packzettel anlegen</button>
          )}
        </li>
      </ul>

      <MaterialBlock plan={plan} />

      {auswahl && (
        <div className="invoice-ablage-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) setAuswahl(null); }}>
          <aside className="apl-auswahl" role="dialog" aria-modal="true" aria-labelledby="apl-auswahl-titel">
            <h2 id="apl-auswahl-titel">Welche Leistungen übernimmt ein Helfer?</h2>
            <p className="apl-leise">Gewählt wird aus dem, was noch beim eigenen Team liegt. Budget, Helfer und Pauschale kommen im nächsten Schritt.</p>
            <ul className="apl-auswahl-liste">
              {eigen.map((p) => (
                <li key={p.id}>
                  <label className="apl-schalter">
                    <input
                      type="checkbox"
                      checked={auswahl.includes(p.id)}
                      onChange={() => setAuswahl((a) => (a!.includes(p.id) ? a!.filter((x) => x !== p.id) : [...a!, p.id]))}
                    />
                    <span>
                      <strong>Pos. {positionsNummer(plan, p.id)} · {p.titel}</strong>
                      <small>{p.menge.toLocaleString("de-DE")} {p.einheit} · {formatEur(p.gesamt_netto, { round: true })}</small>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
            <div className="apl-blatt-knoepfe">
              <button type="button" className="vg-btn" onClick={() => setAuswahl(null)}>Abbrechen</button>
              <button type="button" className="vg-btn vg-btn-primary" disabled={!auswahl.length} onClick={weiter}>
                Weiter mit {auswahl.length === 1 ? "1 Leistung" : `${auswahl.length} Leistungen`}
              </button>
            </div>
          </aside>
        </div>
      )}
      {dialog && (
        <PackzettelDialog
          vorlage={dialog}
          entwurfId=""
          rechnungId={plan.angebot.id}
          betreffVorschlag={`${plan.titel} · zu ${plan.angebot.nummer}`}
          ausfuehrung={{ helfer, pauschalen }}
          onSchliessen={() => setDialog(null)}
          onErstellt={() => qc.invalidateQueries({ queryKey: ["projekt", plan.projekt_id] })}
        />
      )}
    </div>
  );
}

function ZettelZeile({ plan, z, helfer, pauschalen }: {
  plan: Arbeitsplan; z: PlanPackzettel;
  helfer: { id: string; name: string }[];
  pauschalen: { id: string; helfer_id: string; bezeichnung: string; betrag: number }[];
}) {
  const setzen = usePackzettelAusfuehrung(plan.projekt_id);
  const neuePauschale = useAddPauschale();
  const verplant = z.positionen.filter((a) => plan.angebot?.positionen.find((p) => p.id === a)?.tage.length).length;
  const passende = pauschalen.filter((p) => p.helfer_id === z.helfer_id);
  const fehler = setzen.error || neuePauschale.error;

  const pauschaleAnlegen = () => {
    if (!z.helfer_id) return;
    neuePauschale.mutate(
      { projektId: plan.projekt_id, body: { helfer_id: z.helfer_id, betrag: z.budget_netto, bezeichnung: `${z.nummer} ${z.betreff}`.trim() } },
      { onSuccess: (p) => setzen.mutate({ id: z.id, helfer_id: z.helfer_id, pauschale_id: p.id }) },
    );
  };

  return (
    <li className="apl-zeile apl-zeile-zettel">
      <div className="apl-zeile-text">
        <strong>{z.nummer}</strong>
        <small>
          {z.positionen.length === 1 ? "1 Leistung" : `${z.positionen.length} Leistungen`} · {verplant} an einem Tag
          {z.budget_netto ? ` · Budget ${formatEur(z.budget_netto, { round: true })}` : ""}
        </small>
      </div>
      <div className="apl-zeile-wahl">
        <select
          aria-label={`Helfer für ${z.nummer}`}
          value={z.helfer_id}
          disabled={setzen.isPending}
          onChange={(e) => setzen.mutate({ id: z.id, helfer_id: e.target.value, pauschale_id: "" })}
        >
          <option value="">Wer führt aus?</option>
          {helfer.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
        </select>
        {z.helfer_id && (passende.length > 0 ? (
          <select
            aria-label={`Pauschale für ${z.nummer}`}
            value={z.pauschale_id}
            disabled={setzen.isPending}
            onChange={(e) => setzen.mutate({ id: z.id, helfer_id: z.helfer_id, pauschale_id: e.target.value })}
          >
            <option value="">ohne Pauschale</option>
            {passende.map((p) => <option key={p.id} value={p.id}>{p.bezeichnung || "Pauschale"} · {formatEur(p.betrag, { round: true })}</option>)}
          </select>
        ) : (
          <button type="button" className="vg-btn" disabled={neuePauschale.isPending || setzen.isPending || !z.budget_netto} onClick={pauschaleAnlegen}
            title={z.budget_netto ? "Legt die Pauschale unter Personalkosten an und hängt sie an den Packzettel" : "Der Packzettel hat kein Budget"}>
            {z.budget_netto ? `Pauschale ${formatEur(z.budget_netto, { round: true })} anlegen` : "ohne Budget"}
          </button>
        ))}
      </div>
      {fehler && <p className="apl-fehler" role="alert">{fehlerText(fehler)}</p>}
    </li>
  );
}
